/**
 * Transaction-friction engine.
 *
 * Given a jurisdiction and a home price, computes the one-off cash costs
 * incurred at purchase (buyer closing costs + land transfer / stamp duty)
 * and the one-off cash costs incurred at sale (agent commission).
 *
 * These are pure functions of price and jurisdiction — the caller is
 * responsible for feeding the appropriate purchase price to `buyCost`
 * and the (usually different, appreciated) sale price to `sellCost`.
 */

import type { Jurisdiction, TransferTax } from "./jurisdictions";

export interface BuyCostBreakdown {
  /** Non-tax closing costs: lender, legal, inspection, title, etc. */
  readonly closingCosts: number;
  /** Land transfer / stamp duty (0 for jurisdictions with no such tax). */
  readonly transferTax: number;
  /** Sum of closing costs and transfer tax. */
  readonly total: number;
}

export interface SellCostBreakdown {
  /** Selling-agent commission paid out of proceeds. */
  readonly agentCommission: number;
  /** Sum of all sell-side costs (currently equal to `agentCommission`). */
  readonly total: number;
}

/** One-off cash cost of purchasing a home at `price` in the given jurisdiction. */
export function buyCost(price: number, jurisdiction: Jurisdiction): BuyCostBreakdown {
  const closingCosts =
    price * jurisdiction.buyerClosingCosts.percentOfValue +
    jurisdiction.buyerClosingCosts.fixed;
  const transferTax = computeTransferTax(price, jurisdiction.transferTaxOnPurchase);
  return {
    closingCosts,
    transferTax,
    total: closingCosts + transferTax,
  };
}

/** One-off cash cost of selling a home at `salePrice` in the given jurisdiction. */
export function sellCost(salePrice: number, jurisdiction: Jurisdiction): SellCostBreakdown {
  const agentCommission = salePrice * jurisdiction.sellerAgentCommission.percentOfValue;
  return {
    agentCommission,
    total: agentCommission,
  };
}

/**
 * Progressive marginal-rate transfer-tax evaluation. Walks the bracket
 * schedule from the lowest tier upward, applying each tier's marginal
 * rate only to the portion of `value` falling within that tier.
 */
export function computeTransferTax(value: number, tax: TransferTax): number {
  if (tax.kind === "none" || value <= 0) return 0;
  let previousUpper = 0;
  let total = 0;
  for (const bracket of tax.brackets) {
    const upper = bracket.upTo ?? Infinity;
    const amountInBracket = Math.min(value, upper) - previousUpper;
    if (amountInBracket <= 0) break;
    total += amountInBracket * bracket.marginalRate;
    previousUpper = upper;
    if (value <= upper) break;
  }
  return total;
}
